import { Suspense, useMemo, useState } from 'react'
import type {
  AgGridEvent,
  ColDef,
  SelectionChangedEvent
} from 'ag-grid-community'

import AgGridReactLazy from 'common/components/LazyLoaded/AgGridReact'
import { SearchInput } from 'common/components/SearchInput'
import {
  makeRUoptions,
  formatWithCommasOrExponential
} from 'common/utils/chart/chartUtils'
import { ISavedState } from 'features/RiskManagement/Assessments'

export interface IStateDetails {
  stateList: ISavedState[]
  setSelectedStates: React.Dispatch<React.SetStateAction<number[]>>
}

interface IStateRow {
  id: number
  name: string
  totalRisk: number
  date: string
}

const StateDetails = ({ stateList, setSelectedStates }: IStateDetails) => {
  const [searchText, setSearchText] = useState<string>('')

  const [previousSelectedRows, setPreviousSelectedRows] = useState<
    Array<number>
  >([])

  const onGridReady = (evt: AgGridEvent) => {
    evt.api.setGridOption('quickFilterText', searchText)
    evt.api.autoSizeAllColumns()
  }

  const onSelectionChange = (evt: SelectionChangedEvent) => {
    const tempSelectedRowNodes = evt.api.getSelectedNodes()
    const tempSelectedRows = evt.api.getSelectedRows()
    let selectedRowsCount = tempSelectedRowNodes.length

    if (selectedRowsCount > 2) {
      while (selectedRowsCount > 2) {
        for (let i = 0; i < selectedRowsCount; ++i) {
          if (!previousSelectedRows.includes(tempSelectedRows[i].id)) {
            tempSelectedRowNodes[i].setSelected(false)
            selectedRowsCount--
          }
        }
      }
    } else {
      const tempSelectedStateIds: number[] = []

      tempSelectedRows.forEach((row: IStateRow) => {
        stateList.forEach((state) => {
          if (state.name === row.name && state.id === row.id) {
            if (!tempSelectedStateIds.includes(state.id)) {
              tempSelectedStateIds.push(state.id)
            }
          }
        })
      })
      setPreviousSelectedRows(tempSelectedStateIds)
      setSelectedStates(tempSelectedStateIds)
    }
  }

  const totalRisk = (state: ISavedState): number => {
    const tempValues: number[] = []

    state.risks?.items.forEach((riskData) => {
      tempValues.push(riskData.values[0].value)
    })

    return tempValues.reduce((a, b) => a + b, 0)
  }

  function riskFormatter(params: { value: string }): string {
    return formatWithCommasOrExponential(params.value, makeRUoptions('ru'))
  }

  const columnDefs: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      checkboxSelection: true
    },
    {
      headerName: 'Total Risk',
      field: 'totalRisk',
      valueFormatter: riskFormatter
    },
    {
      headerName: 'Date',
      field: 'date'
    }
  ]

  const rows: IStateRow[] = useMemo(() => {
    const sortedStateList = [...stateList].sort((a, b) =>
      b.dateCreated.localeCompare(a.dateCreated)
    )

    return sortedStateList.map((state) => ({
      name: state.name,
      totalRisk: totalRisk(state),
      date: new Date(state.dateCreated).toLocaleString('en-US', {
        month: 'long',
        day: '2-digit',
        year: 'numeric'
      }),
      id: state.id
    }))
  }, [stateList])

  return (
    <div className='m-6'>
      <div className='mb-2 font-bold'>Select 2 Below</div>
      <div className='rounded-sm border border-gray-300 bg-white'>
        <div className='mb-2 ml-4 mr-80 mt-4 flex h-10 bg-white'>
          <SearchInput
            placeholder='Search...'
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div
          className='ag-theme-lighthouse-selection-grid'
          style={{ height: 300, width: 620 }}
        >
          <Suspense fallback='Loading table...'>
            <AgGridReactLazy
              rowData={rows}
              columnDefs={columnDefs}
              rowSelection='multiple'
              onGridReady={onGridReady}
              rowMultiSelectWithClick
              onSelectionChanged={onSelectionChange}
              quickFilterText={searchText}
            />
          </Suspense>
        </div>
      </div>
    </div>
  )
}

export default StateDetails
